import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { useEventImageConsent } from '../../../../app/hooks/event/useEventImageConsent';
import { useArchiveGallery } from '../../../../app/hooks/gallery/useArchiveGallery';
import { useGallery } from '../../../../app/hooks/gallery/useGallery';
import { useGalleryPhotoUpload } from '../../../../app/hooks/gallery/useGalleryPhotoUpload';
import { usePublishGallery } from '../../../../app/hooks/gallery/usePublishGallery';
import { useRemoveGallery } from '../../../../app/hooks/gallery/useRemoveGallery';
import { useRemoveGalleryPhoto } from '../../../../app/hooks/gallery/useRemoveGalleryPhoto';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';
import { IConfirmationModalProps } from '../../ConfirmationModal';

export function useGalleryManagerModalController(galleryId: string) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { gallery, isGalleryLoading } = useGallery(galleryId);
  const { getImageConsent, hasLeadDeniedImageConsent } = useEventImageConsent();
  const leadId = gallery?.leadId ?? '';

  const { queue, enqueue, clearFinished, removeItem, isUploading } =
    useGalleryPhotoUpload(galleryId);
  const { publishGalleryAsync } = usePublishGallery(leadId);
  const { archiveGalleryAsync } = useArchiveGallery(leadId);
  const { removeGalleryAsync } = useRemoveGallery(leadId);
  const { removePhotoAsync } = useRemoveGalleryPhoto(galleryId);

  const {
    handleCloseModal,
    handleOpenConfirmationModal,
    handleCloseConfirmationModal,
  } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
      handleOpenConfirmationModal: state.modal.handleOpenConfirmationModal,
      handleCloseConfirmationModal: state.modal.handleCloseConfirmationModal,
    })),
  );

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      if (files.length > 0) {
        void enqueue(files);
      }
    },
    [enqueue],
  );

  const handleRemovePhoto = useCallback(
    (photoId: string) => {
      void removePhotoAsync({ photoId });
    },
    [removePhotoAsync],
  );

  const handlePublish = useCallback(() => {
    const modalProps: IConfirmationModalProps = {
      title: t('gallery.manageModal.publishConfirm.title'),
      text: t('gallery.manageModal.publishConfirm.text'),
      confirmButtonText: t('gallery.manageModal.publishConfirm.confirm'),
      cancelButtonText: t('gallery.manageModal.publishConfirm.cancel'),
      onCancel: handleCloseConfirmationModal,
      onClose: handleCloseConfirmationModal,
      onConfirm: async () => {
        handleCloseConfirmationModal();
        await publishGalleryAsync(galleryId);
      },
    };
    handleOpenConfirmationModal(modalProps);
  }, [
    t,
    galleryId,
    publishGalleryAsync,
    handleOpenConfirmationModal,
    handleCloseConfirmationModal,
  ]);

  const handleArchive = useCallback(() => {
    const modalProps: IConfirmationModalProps = {
      title: t('gallery.manageModal.archiveConfirm.title'),
      text: t('gallery.manageModal.archiveConfirm.text'),
      confirmButtonText: t('gallery.manageModal.archiveConfirm.confirm'),
      cancelButtonText: t('gallery.manageModal.archiveConfirm.cancel'),
      danger: true,
      onCancel: handleCloseConfirmationModal,
      onClose: handleCloseConfirmationModal,
      onConfirm: async () => {
        handleCloseConfirmationModal();
        await archiveGalleryAsync(galleryId);
      },
    };
    handleOpenConfirmationModal(modalProps);
  }, [
    t,
    galleryId,
    archiveGalleryAsync,
    handleOpenConfirmationModal,
    handleCloseConfirmationModal,
  ]);

  const handleDelete = useCallback(() => {
    const modalProps: IConfirmationModalProps = {
      title: t('gallery.manageModal.deleteConfirm.title'),
      text: t('gallery.manageModal.deleteConfirm.text'),
      confirmButtonText: t('gallery.manageModal.deleteConfirm.confirm'),
      cancelButtonText: t('gallery.manageModal.deleteConfirm.cancel'),
      danger: true,
      onCancel: handleCloseConfirmationModal,
      onClose: handleCloseConfirmationModal,
      onConfirm: async () => {
        handleCloseConfirmationModal();
        await removeGalleryAsync(galleryId);
        handleCloseModal();
      },
    };
    handleOpenConfirmationModal(modalProps);
  }, [
    t,
    galleryId,
    removeGalleryAsync,
    handleCloseModal,
    handleOpenConfirmationModal,
    handleCloseConfirmationModal,
  ]);

  const handleCopyLink = useCallback(async () => {
    if (!gallery?.publicUrl) return;
    await navigator.clipboard.writeText(gallery.publicUrl);
    toast(t('gallery.manageModal.linkCopied'), 'success');
  }, [gallery?.publicUrl, toast, t]);

  return {
    gallery,
    isGalleryLoading,
    imageConsent: getImageConsent(gallery?.eventId),
    hasLinkedEvent: !!gallery?.eventId,
    leadHasDeniedConsent: hasLeadDeniedImageConsent(gallery?.leadId),
    queue,
    isUploading,
    handleFilesSelected,
    clearFinished,
    removeItem,
    handleRemovePhoto,
    handlePublish,
    handleArchive,
    handleDelete,
    handleCopyLink,
  };
}
